import { getDesktopBridge } from "./desktop";
import { trpcClient } from "./trpc";

const DEDUPE_WINDOW_MS = 10_000;
const MAX_REPORTS_PER_SESSION = 50;

const recent = new Map<string, number>();
let sent = 0;

function describe(error: unknown): {
	errorType: string;
	message: string;
	stack?: string;
} {
	if (error instanceof Error) {
		return {
			errorType: error.name || "Error",
			message: error.message || String(error),
			stack: error.stack,
		};
	}
	return { errorType: "UnknownError", message: String(error) };
}

/**
 * Send an error to the API's error tracking (admin → Errors). Best-effort:
 * deduped, capped per session, and never throws.
 */
export function reportError(
	error: unknown,
	context?: Record<string, unknown>
): void {
	try {
		const { errorType, message, stack } = describe(error);
		const key = `${errorType}:${message}`;
		const now = Date.now();
		const last = recent.get(key);
		if (
			(last !== undefined && now - last < DEDUPE_WINDOW_MS) ||
			sent >= MAX_REPORTS_PER_SESSION
		) {
			return;
		}
		recent.set(key, now);
		sent += 1;

		const bridge = getDesktopBridge();
		trpcClient.errors.report
			.mutate({
				source: bridge ? "desktop" : "studio",
				errorType: errorType.slice(0, 200),
				message: message.slice(0, 4000),
				stack: stack?.slice(0, 16_000),
				url: window.location.href.slice(0, 2048),
				appVersion: bridge?.version,
				context: { ...context, platform: bridge?.platform ?? "web" },
			})
			.catch(() => {
				// Reporting is best-effort; never report a failed report.
			});
	} catch {
		// Never let error reporting break the app.
	}
}

/** Uncaught errors and unhandled promise rejections anywhere in the page. */
export function installGlobalErrorReporting(): void {
	window.addEventListener("error", (event) => {
		reportError(event.error ?? event.message, { kind: "window.error" });
	});
	window.addEventListener("unhandledrejection", (event) => {
		reportError(event.reason, { kind: "unhandledrejection" });
	});
}
